import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import Fade from '@mui/material/Fade';
import { GradientTypography } from '../gradients/GradientTypography';
import { useEffect, useMemo, useState } from 'react';
import { Link } from '../Link';
import { getGlobalQueries, pageSearch } from 'src/firebaseCloud';
import { ImageOptimized } from '../image/ImageOptimized';
import { SearchResultCard } from '../cards/SearchResultCard';
import { SearchResult } from 'functions/src/types/firestore/Search';
import { useMobile } from 'src/hooks/useMobile';
import { SocialIcons } from '../socials/SocialIcons';
import { Social } from 'functions/src/types/Social';
import { WizardDrawer } from '../wizard/WizardDrawer';
type MenuItem = { type: 'menuItem'; title: string; link: string };

type MobileDrawerProps = {
  handleClose: () => void;
  menuItems: MenuItem[];
  socials: Social[];
};

type HamburgerHeaderProps = {
  title: string;
};
const HamburgerHeader = ({ title }: HamburgerHeaderProps) => {
  return (
    <Box
      sx={{
        px: 4,
        pt: 3,
        pb: 2,
      }}
    >
      <GradientTypography
        variant="h5"
        sx={{ textTransform: 'uppercase' }}
        gradientColor="primary.vertical"
      >
        {title}
      </GradientTypography>
    </Box>
  );
};

const MenuItem = ({ title, link, handleClose, comingSoon }) => {
  const [hovered, setHovered] = useState(false);

  const theme = useTheme();
  const isMobile = useMobile();
  return (
    <>
      {comingSoon ? (
        <Box
          onMouseEnter={() => {
            return setHovered(true);
          }}
          onMouseLeave={() => {
            return setHovered(false);
          }}
          sx={{
            px: 8,
            py: 2,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
            '&:hover': !isMobile
              ? {
                  background: 'rgba(255,255,255,0.02)',
                  cursor: 'pointer',
                }
              : undefined,
          }}
          onClick={handleClose}
        >
          <Typography
            variant="h6"
            sx={{ color: 'white', textTransform: 'uppercase' }}
          >
            {title}
          </Typography>
          <Fade in={isMobile || hovered}>
            <Typography
              variant="overline"
              sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}
            >
              coming soon
            </Typography>
          </Fade>
        </Box>
      ) : (
        <Link href={link} sx={{ textDecoration: 'none' }}>
          <Box
            sx={{
              px: 8,
              py: 2,
              '&:hover': {
                background: 'rgba(255,255,255,0.02)',
                cursor: 'pointer',
              },
            }}
            onClick={handleClose}
          >
            <Typography
              variant="h6"
              sx={{ color: 'white', textTransform: 'uppercase' }}
            >
              {title}
            </Typography>
          </Box>
        </Link>
      )}
    </>
  );
};

export const MobileDrawer = ({
  handleClose,
  menuItems = [],
  socials = [],
}: MobileDrawerProps) => {
  const [trending, setTrending] = useState<SearchResult[]>([]);
  useEffect(() => {
    const handler = async () => {
      const { data } = await getGlobalQueries();
      const { globalQueries } = data;
      const trendingFromQuery = await Promise.all(
        globalQueries.map(async ({ query }) => {
          const searchResults = await pageSearch({
            searchQuery: query,
            selectedTags: ['game', 'item', 'profile', 'page', 'article'],
          });
          return { ...searchResults.data.hits[0], type: 'trending' };
        }),
      );
      setTrending(trendingFromQuery);
    };
    if (!trending?.length) {
      handler();
    }
  }, [trending?.length]);
  const sections = useMemo(() => {
    return [
      {
        title: 'navigation',
        content: menuItems,
        render: ({ title, link, comingSoon }) => {
          return (
            <MenuItem
              comingSoon={comingSoon}
              title={title}
              link={link}
              handleClose={handleClose}
            />
          );
        },
      },
      {
        title: 'trending',
        content: trending,
        render: (searchResult) => {
          return (
            <Box sx={{ my: 2, px: 4 }}>
              <SearchResultCard searchResult={searchResult} />
            </Box>
          );
        },
      },
    ];
  }, [menuItems, trending, handleClose]);
  return (
    <WizardDrawer open={true} anchor="left">
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {sections.map((section, sectionIdx) => {
          if (section?.content?.length) {
            return (
              <Box
                key={`${section.title}-${sectionIdx}`}
                sx={{ width: '100%' }}
              >
                {section.title ? (
                  <>
                    <Divider />
                    <HamburgerHeader title={section.title} />
                    {section.content.map((content, idx) => {
                      return (
                        <Fade
                          key={idx}
                          in={true}
                          timeout={400 + 100 * (idx + 1) * (sectionIdx + 1)}
                        >
                          <Box>{section.render({ ...content })}</Box>
                        </Fade>
                      );
                    })}
                  </>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      px: 4,
                      py: 2,
                      gap: 8,
                    }}
                  >
                    {section.content.map(({ link, name, src }) => {
                      return (
                        <Fade key={name} in={true} timeout={700}>
                          <Link
                            href={link}
                            sx={{ '&:hover': { cursor: 'pointer' }, my: 2 }}
                          >
                            <Box
                              sx={{
                                width: '40px',
                                height: '40px',
                              }}
                            >
                              <ImageOptimized
                                src={src}
                                width={40}
                                height={40}
                                alt={`${name} icon`}
                              />
                            </Box>
                          </Link>
                        </Fade>
                      );
                    })}
                  </Box>
                )}
                {sectionIdx === 0 && (
                  <Box display="flex" justifyContent="flex-start" p={4}>
                    <SocialIcons linkedSocials={socials} disableMobile={true} />
                  </Box>
                )}
              </Box>
            );
          }
          return null;
        })}
      </Box>
    </WizardDrawer>
  );
};
